import PropTypes from 'prop-types';
// @mui
import { keyframes, styled } from '@mui/material/styles';
import { Tooltip } from '@mui/material';
// utils
import Iconify from '../../components/iconify';

// ----------------------------------------------------------------------

// UAE business line. Kept as a prop so another market can pass its own number
// without touching this component.
const DEFAULT_PHONE = '971526353211';

// Prefilled so the visitor never starts from a blank compose box. `wa.me`
// expects the message URL-encoded in `text`.
const DEFAULT_MESSAGE =
  "Hi ASAP UAE, I'd like to know more about your licensing exam preparation courses.";

const WHATSAPP_BRAND = '#25D366';
const WHATSAPP_BRAND_DARK = '#128C7E';

// One-shot entrance so the button is noticed without looping forever.
const enter = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.9); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// A real anchor rather than a click handler on a div: it is keyboard focusable,
// exposes a real link role, supports middle-click and "copy link address", and
// opens WhatsApp in a new tab instead of replacing the page the visitor is on.
const FabRoot = styled('a')(({ theme }) => ({
  position: 'fixed',
  // Unchanged from the original placement.
  right: '4px',
  bottom: '5px',
  zIndex: theme.zIndex.appBar + 2,
  width: 56,
  height: 56,
  display: 'grid',
  placeItems: 'center',
  borderRadius: '50%',
  color: '#fff',
  backgroundColor: WHATSAPP_BRAND,
  boxShadow: `0 6px 20px ${WHATSAPP_BRAND}59`,
  textDecoration: 'none',
  animation: `${enter} 0.5s cubic-bezier(0.43, 0.13, 0.23, 0.96) 0.4s both`,
  transition:
    'transform 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96), background-color 0.3s ease, box-shadow 0.3s ease',
  // Only animate hover on devices that actually have a pointer, otherwise the
  // button sticks in the hovered state after a tap.
  '@media (hover: hover)': {
    '&:hover': {
      transform: 'translateY(-3px) scale(1.04)',
      backgroundColor: WHATSAPP_BRAND_DARK,
      boxShadow: `0 10px 26px ${WHATSAPP_BRAND}80`,
    },
  },
  '&:focus-visible': {
    outline: `3px solid ${theme.palette.primary.main}`,
    outlineOffset: 3,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    transition: 'none',
  },
}));

// ----------------------------------------------------------------------

WhatsappFLoating.propTypes = {
  phone: PropTypes.string,
  message: PropTypes.string,
  label: PropTypes.string,
  sx: PropTypes.object,
};

export default function WhatsappFLoating({
  phone = DEFAULT_PHONE,
  message = DEFAULT_MESSAGE,
  label = 'Chat with us on WhatsApp',
  sx,
}) {
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

  return (
    <Tooltip title={label} placement="left">
      <FabRoot
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        sx={sx}
      >
        <Iconify icon="ic:baseline-whatsapp" width={30} sx={{ color: 'inherit' }} />
      </FabRoot>
    </Tooltip>
  );
}